import { PizzaSize, PizzaCrust, PizzaIngredient, PizzaConfig } from './pizza-config';

import { Utils } from '../helpers/utils';

export class Pizza {
  config: PizzaConfig;

  size: string;
  category: string;
  flavors: string[] = [];
  flavorProductIds: number[] = [];
  flavorsPrice: number;
  crusts: string[] = [];
  crustsPrice: number;
  ingredients: string[] = [];
  ingredientsPrice: number;
  mode: 'delivery'|'onsite';
  modePrice: number = 0;
  total: number;
  note: string;

  private _auxSize: PizzaSize;
  private _auxFlavorsPrices: {flavorName: string, price: number}[];
  private _auxCanMix: string[];

  constructor(config: PizzaConfig, json: Partial<Pizza> = {}) {
    this.config = config;
    Object.assign(this, {
      size: '',
      category: '',
      flavors: [],
      crusts: [],
      ...json
    });

    this.total = this.flavorsPrice = this.crustsPrice = this.ingredientsPrice = this.modePrice = 0;

    // Ensure arrays are copied (not referenced)
    if (Array.isArray(this.flavors)) this.flavors = [...this.flavors];
    if (Array.isArray(this.flavorProductIds)) this.flavorProductIds = [...this.flavorProductIds];
    if (Array.isArray(this.crusts)) this.crusts = [...this.crusts];
    if (Array.isArray(this.ingredients)) this.ingredients = [...this.ingredients];
  }

  toPayload() {
    return {
      quantity: 1,
      mode: this.mode,
      size: this.size,
      crusts: this.crusts,
      ingredients: this.ingredients,
      note: this.note,
      flavors: this.flavors,
      flavor_product_ids: this.flavorProductIds
    };
  }

  isOK(): boolean {
    if(!this.size || this.flavors.length == 0 || this.total == 0)
      return false;

    return true;
  }

  textRepresentation(): string {
    const bits = [`Pizza ${this.size}`];
    if(this.flavors.length) bits.push(this.flavors.join(' | '));
    if(this.crusts.length) bits.push(`Bordas: ${this.crusts.join(', ')}`);
    if(this.ingredients.length) bits.push(`Add: ${this.ingredients.join(', ')}`);
    if(this.note) bits.push(`OBS: ${this.note}`);
    if(this.mode == 'delivery') bits.push('(viagem)');
    return bits.join(' · ');
  }
  setMode(mode: 'delivery'|'onsite') {
    this.mode = mode;
    if(mode == 'delivery' && this._auxSize) {
      this.modePrice = this._auxSize.packaging_price || 0;
    } else {
      this.modePrice = 0;
    }
  }
  setSize(sizeName: string): PizzaSize|null {
    const found: PizzaSize = this.config.sizes.find(size => size.name === sizeName);
    if(!found) {
      console.error(`Pizza::setSize -> size ${sizeName} does not exist on pizza_config`, this.config);
      return null;
    }
    this.size = sizeName;
    this._auxSize = found;
    this.modePrice = this.mode == 'delivery' ? found.packaging_price || 0 : 0;

    return found;
  }
  // return the categories's names that can be mixed with this flavor, max flavors and max crusts
  setFirstFlavor(category: string, flavor: string, prices: {size_name: string, price: number}[], productId?: number): {canMix: string[], maxFlavors: number, maxCrusts: number} {
    let maxFlavors: number = 1;
    let maxCrusts: number = 1;
    if(this._auxSize){
      maxFlavors = this._auxSize.max_flavors;
      maxCrusts = this._auxSize.max_crust_flavors;
    } else {
      let msg = `Pizza::setFirstFlavor -> you should call setSize before selecting flavors`;
      console.error(msg, Utils.clone(this));
      throw new Error(msg);
    }

    this.category = category;
    this.flavors = [flavor];
    this.flavorProductIds = productId ? [productId] : [];
    this.flavorsPrice = this._getPrice(this._auxSize.name, prices);
    this._auxFlavorsPrices = [{flavorName: flavor, price: this.flavorsPrice}];
    this._calculateTotal();
    this._auxCanMix = this.config.flavor_categories;


    if(this.config.categories_mixing == "all")
      return {canMix: this._auxCanMix, maxFlavors, maxCrusts};

    let pairs: string[] = this.config.categories_mixing.split(";");
    let auxCat = category.split("Pizzas ")[1];
    let canMix: string[] = [auxCat];
    for(let pair of pairs) {
      let auxFlavors = pair.split("|");
      if(auxFlavors[0] == auxCat){
        Utils.pushIfNotExists(canMix, auxFlavors[1]);
      } else if(auxFlavors[1] == auxCat){
        Utils.pushIfNotExists(canMix, auxFlavors[0]);
      }
    }

    for(let i = 0; i < canMix.length; i++) {
      if(canMix[i].indexOf("Pizzas ") == -1)
        canMix[i] = "Pizzas " + canMix[i];
    }

    this._auxCanMix = canMix;
    return {canMix, maxFlavors, maxCrusts};
  }
  setAnotherFlavor(category: string, flavor: string, prices: {size_name: string, price: number}[], productId?: number): {success: boolean, canAddMore: boolean} {
    if(this.flavors.length < 1) {
      let msg = "Pizza::setAnotherFlavor -> you should call setFirstFlavor before selecting more flavors";
      console.error(msg, Utils.clone(this));
      throw new Error(msg);
    }
    if(this._auxCanMix.indexOf(category) == -1) {
      let msg = `Pizza::setAnotherFlavor -> cannot mix category '${category}'`;
      console.error(msg, category, this._auxCanMix);
      throw new Error(msg);
    }

    let maxFlavors: number = 1;
    if(this._auxSize){
      maxFlavors = this._auxSize.max_flavors;
    } else {
      console.warn("Pizza::setAnotherFlavor -> you should call setSize before selecting flavors", Utils.clone(this));
    }
    let success = false;
    let canAddMore = false;
    if(maxFlavors > this.flavors.length) {
      this.flavors.push(flavor);
      if(productId) {
        this.flavorProductIds.push(productId);
      }
      const price = this._getPrice(this._auxSize.name, prices);
      this._auxFlavorsPrices.push({flavorName: flavor, price: price});
      success = true;
      canAddMore = maxFlavors > this.flavors.length;

      if(this.config.multi_flavor_pricing === "highest price") {
        this.flavorsPrice = Math.max(this.flavorsPrice, price);
      } else {
        this.flavorsPrice = 0;
        for(let i = 0; i < this._auxFlavorsPrices.length; i++) {
          this.flavorsPrice += Number((this._auxFlavorsPrices[i].price / this._auxFlavorsPrices.length).toFixed(2));
        }
      }
    }
    this._calculateTotal();
    return { success, canAddMore };
  }
  clearFlavors() {
    this.category = "";
    this.flavors = [];
    this.flavorProductIds = [];
    this._auxFlavorsPrices = [];
    this._auxCanMix = [];
    this.flavorsPrice = this.modePrice = 0;
    this._calculateTotal();
  }
  setCrusts(crusts: string[]): {success: boolean, price: number} {
    if(!this._auxSize) {
      let msg = `Pizza::setCrusts -> you must call setSize(sizeName) first`;
      console.error(msg, Utils.clone(this.config));
      return {success: false, price: 0};
    }
    const sizeName = this._auxSize.name;
    if(crusts.length > this._auxSize.max_crust_flavors) {
      console.error(`Pizza::setCrusts -> you can only add ${this._auxSize.max_crust_flavors}, you tried adding ${crusts.length}`, crusts);
      return {success: false, price: 0};
    }

    let price = 0;
    for(let crust of crusts) {
      const found = this.config.crusts.find(crustObj => crustObj.name === crust)
      if(found)
        price += this._getPrice(sizeName, found.prices);
      else {
        console.error(`Pizza::setCrusts -> invalid crust name '${crust}'`, Utils.clone(this.config));
        return {success: false, price: 0};
      }
    }
    this.crusts = crusts;
    this.crustsPrice = price;

    this._calculateTotal();
    return {success: true, price: this.crustsPrice};
  }
  setIngredients(ingredients: string[]): {success: boolean, price: number} {
    if(!this._auxSize) {
      let msg = `Pizza::setIngredients -> you must call setSize(sizeName) first`;
      console.error(msg, Utils.clone(this.config));
      return {success: false, price: 0};
    }
    const sizeName = this._auxSize.name;

    this.ingredientsPrice = 0;
    this.ingredients = [];
    for(let ingredient of ingredients) {
      const found = this.config.ingredients.find(ingObj => ingObj.name === ingredient);
      if(found) {
        this.ingredients.push(ingredient);
        this.ingredientsPrice += this._getPrice(sizeName, found.prices);
      } else {
        let msg = `Pizza::setIngredients -> ingredient ${ingredient} not found on pizza_config`;
        console.error(msg, Utils.clone(this.config));
        return {success: false, price: 0};
      }
    }
    this._calculateTotal();
    return {success: true, price: this.ingredientsPrice};
  }

  private _calculateTotal() {
    this.total = Number((this.flavorsPrice + this.crustsPrice + this.ingredientsPrice + this.modePrice).toFixed(2));
  }
  private _getPrice(sizeName: string, prices: {size_name: string, price: number}[]): number {
    let found = prices.find(priceObj => priceObj.size_name === this._auxSize.name);
    if(!found) {
      let msg = `Cannot find price for size '${sizeName}'`;
      console.error(msg, prices);
      throw new Error(msg);
    }
    return found.price;
  }
}
